import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { LicenseHostMap } from '@portal-core/license-host-maps/models/license-host-map.model';
import { LicenseHostMapSslCertificate } from '@portal-core/license-host-maps/models/license-host-map-ssl-certificate.model';
import { LicenseHostMapUpdateRobotsExclusion } from '@portal-core/license-host-maps/models/license-host-map-update-robots-exclusion.model';
import { LicenseHostMapsApiService } from '@portal-core/license-host-maps/services/license-host-maps-api.service';
import { LicenseHostMapsDataService } from '@portal-core/license-host-maps/services/license-host-maps-data.service';
import { Observable, map, switchMap } from 'rxjs';

@Service()
export class LicenseHostMapsService extends CollectionServiceBase<LicenseHostMap> {
  private readonly licenseHostMapApiService = inject(LicenseHostMapsApiService);

  constructor() {
    super(inject(LicenseHostMapsDataService));
  }

  getLicenseHostMap$(licenseHostMapId: number): Observable<LicenseHostMap> {
    return this.licenseHostMapApiService.getLicenseHostMap$(licenseHostMapId);
  }

  getCNameValidation$(licenseId: number, domain: string, legacyBinding: boolean, licenseHostMapId?: number): Observable<MadCloudResult<void>> {
    return this.licenseHostMapApiService.getCNameValidation$(licenseId, domain, legacyBinding, licenseHostMapId);
  }

  getLicenseHostMapSslCertificate$(licenseHostMapId: number): Observable<LicenseHostMapSslCertificate> {
    return this.licenseHostMapApiService.getLicenseHostMapSslCertificate$(licenseHostMapId);
  }

  getLicenseHostMapDomainHealth$(licenseHostMapId: number): Observable<MadCloudResult<void>> {
    return this.licenseHostMapApiService.getLicenseHostMapDomainHealth$(licenseHostMapId);
  }

  getDefaultRobotsText$(licenseHostMapId: number): Observable<string> {
    return this.licenseHostMapApiService.getDefaultRobotsText$(licenseHostMapId);
  }

  addLicenseHostMap$(licenseId: number, licenseHostMap: LicenseHostMap, email: string, password: string): Observable<LicenseHostMap> {
    return this.licenseHostMapApiService.addLicenseHostMap$(licenseId, licenseHostMap, email, password);
  }

  updateLicenseHostMap$(licenseId: number, licenseHostMap: LicenseHostMap, email: string, password: string): Observable<LicenseHostMap> {
    return this.licenseHostMapApiService.updateLicenseHostMap$(licenseId, licenseHostMap, email, password).pipe(
      switchMap(newLicenseHostMap => {
        return this.updateItems$([newLicenseHostMap]).pipe(
          map(() => newLicenseHostMap)
        );
      })
    );
  }

  deleteLicenseHostMap$(licenseId: number, licenseHostMapId: number): Observable<MadCloudResult<void>> {
    return this.licenseHostMapApiService.deleteLicenseHostMap$(licenseId, licenseHostMapId).pipe(
      switchMap(result => {
        return this.updateItems$({
          [licenseHostMapId]: {
            InProcess: true,
            IsDeleted: true
          }
        }).pipe(
          map(() => result)
        );
      })
    );
  }

  setLicenseHostMapDefaultSite$(licenseHostMapId: number, defaultSiteId: number): Observable<void> {
    return this.licenseHostMapApiService.setLicenseHostMapDefaultSite$(licenseHostMapId, defaultSiteId).pipe(
      switchMap(() => this.updateItems$({
        [licenseHostMapId]: {
          DefaultSiteId: defaultSiteId
        }
      }))
    );
  }

  setLicenseHostMapRobotsExclusion$(licenseHostMapId: number, updateModel: LicenseHostMapUpdateRobotsExclusion): Observable<MadCloudResult<void>> {
    return this.licenseHostMapApiService.setLicenseHostMapRobotsExclusion$(licenseHostMapId, updateModel).pipe(
      switchMap(result => {
        return this.updateItems$({
          [licenseHostMapId]: {
            RobotsExclusionId: updateModel.RobotsExclusionId,
            RobotsFileOption: updateModel.RobotsFileOption
          }
        }).pipe(
          map(() => result)
        );
      })
    );
  }
}
